import React from 'react';
import { XMarkIcon, EyeIcon } from '@heroicons/react/24/outline';
import Button from '../ui/Button';

interface UpdateTokenModalProps {
  showModal: boolean;
  onClose: () => void;
  updateForm: {
    name: string;
    domains: string;
    expirationDate: string;
    readPermission: boolean;
    writePermission: boolean;
    status: boolean;
  };
  onFormChange: (field: keyof UpdateTokenModalProps['updateForm'], value: any) => void; // Fixed type
  onSubmit: () => void;
  showExpirationDateInput: boolean;
  setShowExpirationDateInput: (show: boolean) => void;
  updateDomainTags: string[];
  setUpdateDomainTags: (tags: string[]) => void;
  updateDomainInput: string;
  setUpdateDomainInput: (input: string) => void;
  updateDomainError: string;
  setUpdateDomainError: (error: string) => void;
  editingDomainIdx: number | null;
  setEditingDomainIdx: (idx: number | null) => void;
  editingDomainValue: string;
  setEditingDomainValue: (value: string) => void;
}

const UpdateTokenModal: React.FC<UpdateTokenModalProps> = ({
  showModal,
  onClose,
  updateForm,
  onFormChange,
  onSubmit,
  showExpirationDateInput,
  setShowExpirationDateInput,
  updateDomainTags,
  setUpdateDomainTags,
  updateDomainInput,
  setUpdateDomainInput,
  updateDomainError,
  setUpdateDomainError,
  editingDomainIdx,
  setEditingDomainIdx,
  editingDomainValue,
  setEditingDomainValue,
}) => {
  if (!showModal) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="w-full max-w-md bg-white rounded-lg shadow-xl">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-neutral-200">
          <h3 className="text-lg font-semibold text-black">Update Token</h3>
          <button onClick={onClose} className="p-1 transition-colors rounded-md text-neutral-400 hover:text-primary-700 hover:bg-primary-50 focus:outline-none focus-visible:border-primary-700 border border-transparent">
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6 space-y-6">
          {/* Token Name */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Token Name
            </label>
            <input
              type="text"
              value={updateForm.name}
              onChange={(e) => onFormChange('name', e.target.value)}
              className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:border-primary-500"
            />
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Status
            </label>
            <select
              value={updateForm.status ? "active" : "inactive"}
              onChange={e => onFormChange('status', e.target.value === "active")}
              className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:border-primary-500"
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          {/* Allowed Domains */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Allowed Domains
            </label>
            <div className="flex flex-wrap items-center border border-neutral-300 rounded-md p-2 space-x-2">
              <div className="flex flex-wrap items-center gap-2">
                {updateDomainTags.map((tag, idx) => (
                  <div key={idx} className="flex items-center bg-primary-100 text-primary-700 px-2 py-1 rounded-full text-sm">
                    {editingDomainIdx === idx ? (
                      <input
                        type="text"
                        value={editingDomainValue}
                        autoFocus
                        onChange={e => setEditingDomainValue(e.target.value)}
                        onBlur={() => {
                          if (
                            editingDomainValue.trim() &&
                            !updateDomainTags.includes(editingDomainValue.trim())
                          ) {
                            const newTags = [...updateDomainTags];
                            newTags[idx] = editingDomainValue.trim();
                            setUpdateDomainTags(newTags);
                          }
                          setEditingDomainIdx(null);
                        }}
                        onKeyDown={e => {
                          if (e.key === 'Enter' || e.key === 'Tab') {
                            e.preventDefault();
                            if (
                              editingDomainValue.trim() &&
                              !updateDomainTags.includes(editingDomainValue.trim())
                            ) {
                              const newTags = [...updateDomainTags];
                              newTags[idx] = editingDomainValue.trim();
                              setUpdateDomainTags(newTags);
                            }
                            setEditingDomainIdx(null);
                          }
                          if (e.key === 'Escape') {
                            setEditingDomainIdx(null);
                          }
                        }}
                        className="border-none outline-none bg-primary-100 text-primary-700 px-1"
                      />
                    ) : (
                      <>
                        <span
                          onClick={() => {
                            setEditingDomainIdx(idx);
                            setEditingDomainValue(tag);
                          }}
                          className="cursor-pointer"
                          title="Click to edit"
                        >
                          {tag}
                        </span>
                        <button
                          type="button"
                          className="ml-2 text-primary-700 hover:text-primary-700"
                          onClick={() => {
                            setUpdateDomainTags(updateDomainTags.filter((_, i) => i !== idx));
                            setUpdateDomainError('');
                          }}
                        >
                          &times;
                        </button>
                      </>
                    )}
                  </div>
                ))}
              </div>
              <input
                type="text"
                value={updateDomainInput}
                onChange={e => setUpdateDomainInput(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && updateDomainInput.trim() !== '') {
                    e.preventDefault();
                    if (updateDomainTags.length >= 5) {
                      setUpdateDomainError('You can only add up to 5 domains.');
                      return;
                    }
                    if (!updateDomainTags.includes(updateDomainInput.trim())) {
                      setUpdateDomainTags([...updateDomainTags, updateDomainInput.trim()]);
                      setUpdateDomainInput('');
                      setUpdateDomainError('');
                    }
                  }
                }}
                placeholder="Add a domain and press Enter"
                className="flex-1 border-none outline-none focus:ring-0"
              />
            </div>
            {updateDomainError && <p className="mt-2 text-sm text-danger-500">{updateDomainError}</p>}
            <p className="mt-1 text-xs text-neutral-500">You can add up to 5 domains.</p>
          </div>

          {/* Expiration Date */}
          <div>
            <div className='flex items-center gap-1 mb-3'>
              <input
                type="checkbox"
                id="updateExpirationToggle"
                checked={showExpirationDateInput}
                onChange={(e) => setShowExpirationDateInput(e.target.checked)}
                className="w-4 h-4 px-2 text-primary-700 rounded focus:border-primary-500"
              />
              <label htmlFor="updateExpirationToggle" className="text-sm text-neutral-700">
                Set Expiration Date
              </label>
            </div>
            <div className="relative border">
              <input
                type="date"
                disabled={!showExpirationDateInput}
                value={updateForm.expirationDate}
                onChange={(e) => onFormChange('expirationDate', e.target.value)}
                className="w-full px-3 py-2 pr-10 border border-neutral-300 rounded-md focus:outline-none focus:border-primary-500"
              />
            </div>
            <p className="mt-1 text-xs text-neutral-500">Leave empty for no expiration</p>
          </div>

          {/* Permissions */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-3">
              Permissions
            </label>
            <div className="grid grid-cols-2 gap-3">
              {/* Read Permission */}
              <label className="flex items-center p-3 border border-neutral-300 rounded-md cursor-pointer hover:bg-neutral-50">
                <input
                  type="checkbox"
                  checked={updateForm.readPermission}
                  onChange={(e) => onFormChange('readPermission', e.target.checked)}
                  className="w-4 h-4 text-primary-700 border-neutral-300 rounded focus:border-primary-500"
                />
                <div className="ml-3 flex items-center">
                  <EyeIcon className="w-4 h-4 text-neutral-500 mr-2" />
                  <span className="text-sm font-medium text-neutral-700">Read</span>
                </div>
              </label>
              {/* Write Permission */}
              <label className="flex items-center p-3 border border-neutral-300 rounded-md cursor-pointer hover:bg-neutral-50">
                <input
                  type="checkbox"
                  checked={updateForm.writePermission}
                  onChange={(e) => onFormChange('writePermission', e.target.checked)}
                  className="w-4 h-4 text-primary-700 border-neutral-300 rounded focus:border-primary-500"
                />
                <div className="ml-3 flex items-center">
                  <span className="text-sm font-medium text-neutral-700">Write</span>
                </div>
              </label>
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="flex justify-end space-x-3 p-6 border-t border-neutral-200">
          <Button
            variant="neutral"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            onClick={onSubmit}
          >
            Update
          </Button>
        </div>
      </div>
    </div>
  );
};

export default UpdateTokenModal;